import React, {
  ChangeEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'

import AutosizeInput from 'react-input-autosize'
import { useDebounce } from 'use-debounce'
import { isEqual } from 'lodash'

import { Box, AutocompleteDropdown } from 'front-end/src/components'
import { formColors, brandColors } from 'shared/basicPalette'
import ChipsInputList from 'front-end/src/components/common/chips-input/chips-input-list/ChipsInputList'
import { useLooopTheme } from 'front-end/src/public-api/hooks/useLooopTheme'
import { useClickAway } from 'front-end/src/components/utils/useClickAway'
import { BoxProps } from '@chakra-ui/react'
import { css } from '@emotion/react'

type ChipsInputProps = {
  helpOptions?: string[]
  isLoading?: boolean
  handleChange: (newValue: string[]) => void
  defaultValue?: string[]
  invalidList?: string[]
  fetchOptions?: (query: string) => void
  isDisabled?: boolean
  value?: string[]
  allowFreeText?: boolean
  placeholder?: string
} & BoxProps

const SEARCH_DELAY = 250

export const ChipsInput = ({
  defaultValue,
  handleChange,
  invalidList = [],
  fetchOptions,
  helpOptions = [],
  isDisabled = false,
  allowFreeText = false,
  value = [],
  placeholder,
  ...rest
}: ChipsInputProps) => {
  const [inputValue, setInputValue] = useState('')
  const [debouncedQuery] = useDebounce(inputValue, SEARCH_DELAY)

  const [chipsData, setChipsData] = useState<string[]>(
    value || defaultValue || []
  )
  const [isInputFocused, setFocused] = useState(false)
  const [isOpenedAutocomplete, setOpenedAutocomplete] = useState(false)
  const wrapperRef = useRef(null)
  const { theme } = useLooopTheme()

  const hasChips = chipsData.length > 0
  const isUpdated = !isEqual(value, chipsData)

  const closeAutoComplete = () => {
    if (!isInputFocused) {
      setOpenedAutocomplete(false)
    }
  }

  const handleChangeInput: ChangeEventHandler<HTMLInputElement> = (event) => {
    const withoutComa = event.target.value.replace(/,$/, '')

    setInputValue(withoutComa)
  }

  const addChip = useCallback(
    (newValue: string) => {
      const isFromAutoComplete =
        allowFreeText || (helpOptions && helpOptions.includes(newValue))
      const isNewValue = !chipsData.includes(newValue)

      if (isFromAutoComplete && isNewValue) {
        const newData = [...chipsData, newValue]
        setChipsData(newData)
        handleChange(newData)
      }

      setInputValue('')
    },
    [setChipsData, chipsData, helpOptions]
  )

  const handleKeyDown = (event: KeyboardEvent) => {
    const { key } = event
    const isSeparatorPressed = key === ',' || key === 'Enter'
    const isBackspacePressed = key === 'Backspace'

    if (hasChips && !inputValue && isBackspacePressed) {
      removeChip(chipsData[chipsData.length - 1])
    }

    if (isSeparatorPressed && inputValue) {
      if (helpOptions && helpOptions.length > 0) {
        const currentActive = (
          document.getElementsByClassName('optionActive')[0] as HTMLElement
        )?.dataset.option
        addChip(currentActive || helpOptions[0])
      } else {
        addChip(inputValue)
      }
    }
  }

  const removeChip = useCallback(
    (textValue: string) => {
      const newData = chipsData.filter((chip) => chip !== textValue)
      setChipsData(newData)
      handleChange(newData)
    },
    [setChipsData, chipsData]
  )

  const handleCloseClick = useCallback(
    (_, textValue?: string) => {
      if (textValue) removeChip(textValue)
    },
    [removeChip]
  )

  useClickAway(isOpenedAutocomplete, wrapperRef, closeAutoComplete)

  useEffect(() => {
    if (fetchOptions) {
      fetchOptions(debouncedQuery)
    }
  }, [debouncedQuery])

  useEffect(() => {
    if (helpOptions) {
      setOpenedAutocomplete(helpOptions.length > 0)
    }
  }, [helpOptions])

  useEffect(() => {
    if (!isUpdated) return

    if (!value.length && inputValue) setInputValue('')
    setChipsData(value)
  }, [value, isUpdated])

  return (
    <Box
      {...rest}
      ref={wrapperRef}
      display="flex"
      padding="6px 8px"
      position="relative"
      flexWrap="wrap"
      fontSize="14px"
      backgroundColor={
        isInputFocused ? formColors.hoverBgColor : formColors.bgColor
      }
      opacity={isDisabled ? 0.5 : 1}
      pointerEvents={isDisabled ? 'none' : 'auto'}
      border="1px solid"
      borderColor={
        isInputFocused ? theme.colors.buttonPrimary.mid : 'transparent'
      }
      borderRadius={theme.radii.base}
      _hover={{
        backgroundColor: formColors.hoverBgColor
      }}
    >
      <Box
        as="label"
        htmlFor="chips-input"
        tabIndex={-1}
        pos="absolute"
        width="100%"
        height="100%"
        top="0"
        left="0"
      />
      <ChipsInputList
        chipsList={chipsData}
        handleCloseClick={handleCloseClick}
        invalidList={invalidList}
      />
      <Box
        maxW="100%"
        overflowX="hidden"
        zIndex="1"
        border="none"
        css={css`
          input::placeholder {
            color: ${brandColors.grey[400]};
          }
        `}
      >
        <AutosizeInput
          id="chips-input"
          inputStyle={{
            border: 'none',
            appearance: 'none',
            backgroundColor: 'transparent',
            height: 24
          }}
          value={inputValue}
          onChange={handleChangeInput}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          disabled={isDisabled}
          placeholder={placeholder}
        />
      </Box>
      <AutocompleteDropdown
        isOpened={isOpenedAutocomplete}
        setOpen={setOpenedAutocomplete}
        options={helpOptions}
        value={inputValue}
        setValue={addChip}
        enableArrowSelection
        withHighlightSearch
      />
    </Box>
  )
}
